import type {
  ConsumptionEvent,
  CreateDrinkEventRequest,
  DeviceIdentity,
  DrinkEvent,
  SessionBootstrap,
} from '@lbc/shared';
import { useCallback, useRef, type Dispatch, type SetStateAction } from 'react';
import type { QueryClient } from '@tanstack/react-query';

import { api } from '../../services/api/client';
import {
  consumptionOutbox,
  drinkMutationQueue,
  eventQueue,
  type PendingDrinkMutation,
} from '../../services/persistence/event-queue';
import type { LocalInstallation } from '../../services/persistence/identity-store';

export interface DashboardSyncPendingOptions {
  identity: DeviceIdentity | null;
  installation: LocalInstallation | null;
  isOnline: boolean;
  queryClient: QueryClient;
  setPending: Dispatch<SetStateAction<CreateDrinkEventRequest[]>>;
  setPendingMutations: Dispatch<SetStateAction<PendingDrinkMutation[]>>;
  setSyncing: Dispatch<SetStateAction<boolean>>;
  isRetryableError: (error: unknown) => boolean;
  onSyncError: (error: unknown) => void;
}

export function useDashboardSyncPending({
  identity,
  installation,
  isOnline,
  queryClient,
  setPending,
  setPendingMutations,
  setSyncing,
  isRetryableError,
  onSyncError,
}: DashboardSyncPendingOptions): () => Promise<void> {
  const syncPendingRef = useRef<Promise<void> | null>(null);

  return useCallback(async () => {
    if (syncPendingRef.current !== null) {
      return syncPendingRef.current;
    }
    const task = (async () => {
      if (!isOnline || identity === null) {
        return;
      }
      setSyncing(true);
      const syncedEvents: Array<DrinkEvent | ConsumptionEvent> = [];
      try {
        if (installation === null) {
          const queued = await eventQueue.list();
          for (const event of queued) {
            try {
              const synced = await api.createDrinkEvent(event);
              await eventQueue.remove(event.id);
              syncedEvents.push(synced);
            } catch (error) {
              if (isRetryableError(error)) {
                break;
              }
              onSyncError(error);
              break;
            }
          }
        } else {
          const queued = await consumptionOutbox.list();
          for (const command of queued) {
            try {
              const synced = await api.createConsumption({
                ...command.event,
                audienceMembershipIds: command.membershipIds,
              });
              await consumptionOutbox.remove(command.id);
              syncedEvents.push(synced);
            } catch (error) {
              if (isRetryableError(error)) {
                break;
              }
              onSyncError(error);
              break;
            }
          }
        }

        const queuedMutations = (await drinkMutationQueue.list()).filter(
          (mutation) =>
            mutation.groupId === identity.groupId &&
            mutation.participantId === identity.participantId,
        );
        for (const mutation of queuedMutations) {
          try {
            if (mutation.operation === 'update') {
              if (installation === null) {
                await api.updateDrinkEvent(mutation.eventId, {
                  occurredAt: mutation.occurredAt,
                  volumeMl: mutation.volumeMl,
                  abv: mutation.abv,
                });
              } else {
                await api.updateConsumption(mutation.eventId, {
                  occurredAt: mutation.occurredAt,
                  volumeMl: mutation.volumeMl,
                  abv: mutation.abv,
                });
              }
            } else if (installation === null) {
              await api.deleteDrinkEvent(mutation.eventId);
            } else {
              await api.deleteConsumption(mutation.eventId);
            }
            await drinkMutationQueue.remove(mutation.eventId);
            setPendingMutations((current) =>
              current.filter((item) => item.eventId !== mutation.eventId),
            );
          } catch (error) {
            if (!isRetryableError(error)) {
              onSyncError(error);
            }
            break;
          }
        }

        if (syncedEvents.length > 0 || queuedMutations.length > 0) {
          try {
            await queryClient.invalidateQueries({ queryKey: ['bootstrap'] });
            await queryClient.invalidateQueries({ queryKey: ['consumptions'] });
          } finally {
            // Keep the optimistic event in `pending` until the refetch has settled. The
            // server response is then written back into the cache so a stale response
            // cannot briefly reset the counter to zero.
            for (const event of syncedEvents) {
              applySyncedEventToCache(
                queryClient,
                identity.groupId,
                installation?.installationId,
                event,
              );
            }
            const syncedIds = new Set(syncedEvents.map((event) => event.id));
            setPending((current) => current.filter((item) => !syncedIds.has(item.id)));
          }
        }
      } finally {
        setSyncing(false);
      }
    })();
    syncPendingRef.current = task;
    try {
      await task;
    } finally {
      if (syncPendingRef.current === task) {
        syncPendingRef.current = null;
      }
    }
  }, [
    identity,
    installation,
    isOnline,
    isRetryableError,
    onSyncError,
    queryClient,
    setPending,
    setPendingMutations,
    setSyncing,
  ]);
}

function applySyncedEventToCache(
  queryClient: QueryClient,
  groupId: string,
  installationId: string | undefined,
  event: DrinkEvent | ConsumptionEvent,
): void {
  let drinkEvent: DrinkEvent;
  if ('projections' in event) {
    if (installationId !== undefined) {
      queryClient.setQueryData<ConsumptionEvent[]>(['consumptions', installationId], (current) =>
        upsertById(current ?? [], event),
      );
    }
    const projection = event.projections.find((item) => item.groupId === groupId);
    if (projection === undefined) {
      return;
    }
    drinkEvent = {
      id: projection.drinkEventId,
      groupId: projection.groupId,
      participantId: projection.participantId,
      occurredAt: event.occurredAt,
      volumeMl: event.volumeMl,
      abv: event.abv,
      createdAt: event.createdAt,
      updatedAt: event.updatedAt,
      deletedAt: event.deletedAt,
    };
  } else {
    drinkEvent = event;
  }

  queryClient.setQueryData<SessionBootstrap>(['bootstrap', groupId], (current) => {
    if (current === undefined) {
      return current;
    }
    return {
      ...current,
      drinkEvents: upsertById(current.drinkEvents, drinkEvent),
    };
  });
}

function upsertById<T extends { id: string }>(items: T[], item: T): T[] {
  const index = items.findIndex((current) => current.id === item.id);
  if (index === -1) {
    return [...items, item];
  }
  const next = [...items];
  next[index] = item;
  return next;
}
